import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Seo from "../components/Seo";
import { PricingSection } from "../components/PricingSection";
import { PRICING_FAQ } from "../data/pricing";

const PRICING_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Fast Estimate",
    url: "https://getfastestimate.com",
    logo: "https://getfastestimate.com/icon-192.png",
    description: "AI-powered HVAC estimating software for contractors — $99.99/month flat rate, 7-day free trial.",
    slogan: "Photo to signed HVAC estimate in two minutes",
    founder: { "@type": "Person", name: "Scott Herberg", description: "HVAC contractor, 28 years in the trade" },
    email: "info@herbergllc.com",
    address: { "@type": "PostalAddress", streetAddress: "30 Gould St Suite R", addressLocality: "Sheridan", addressRegion: "WY", postalCode: "82801", addressCountry: "US" },
    areaServed: [{ "@type": "Country", name: "United States" }, { "@type": "Country", name: "Canada" }],
    makesOffer: {
      "@type": "Offer", name: "Fast Estimate Pro — flat monthly plan", price: "99.99", priceCurrency: "USD",
      description: "Unlimited estimates and unlimited technician seats for the whole company. 7-day free trial, no credit card required.",
      url: "https://getfastestimate.com/pricing",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: PRICING_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
];

export default function Pricing() {
  const { user } = useAuth();

  return (
    <div className="bg-white">
      <Seo
        title="Pricing — Fast Estimate | AI HVAC Estimating Software"
        description="One flat plan for HVAC contractors: $99.99/month, everything included. Unlimited estimates and technicians. 7-day free trial, no credit card."
        path="/pricing"
        jsonLd={PRICING_JSONLD}
      />
      <SiteNav />

      <div className="mx-auto max-w-3xl px-4 pt-16 text-center sm:px-6">
        <h1 className="font-heading text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">Pricing that pays for itself on the first job</h1>
        <p className="mt-4 text-lg text-slate-600">One flat rate: $99.99/month for your whole company. Start with a 7-day free trial — no credit card. Cancel anytime.</p>
      </div>

      <PricingSection user={user} />

      <section className="mx-auto max-w-3xl px-4 pb-4 sm:px-6" data-testid="pricing-math">
        <h2 className="font-heading text-2xl font-black text-slate-900">Do the math on one job</h2>
        <p className="mt-3 text-base leading-relaxed text-slate-600">
          The average residential AC replacement runs $6,000–$12,000. If faster, more professional quotes win you
          just one extra replacement a year, Fast Estimate paid for itself ten times over. Most contractors tell us
          the real win is smaller and daily: no more writing quotes at the kitchen table at 9pm, no more "I'll get
          you a number tomorrow" while a competitor closes the job the same afternoon.
        </p>
        <p className="mt-3 text-base leading-relaxed text-slate-600">
          Every plan feature works from your phone in the driveway: snap photos of the equipment, let the AI draft
          line items from your own price book, offer Good/Better/Best options, collect the e-signature and deposit
          on the spot, and follow the job through scheduling, crew assignment, the final invoice and the automatic
          review request. One subscription, the whole close loop.
        </p>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6" data-testid="pricing-faq">
        <h2 className="font-heading text-2xl font-black text-slate-900">Pricing questions, answered straight</h2>
        <div className="mt-6 space-y-5">
          {PRICING_FAQ.map((f) => (
            <div key={f.q} className="rounded-2xl border-2 border-slate-100 p-6">
              <h3 className="font-heading text-base font-bold text-slate-900">{f.q}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-indigo-500">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-black text-white sm:text-4xl">Ready to quote faster?</h2>
          <p className="mt-3 text-lg text-indigo-100">Start your 7-day free trial today. No credit card required.</p>
          <Link to="/signup"><Button data-testid="pricing-cta-signup" className="mt-8 h-14 rounded-xl bg-white px-8 text-lg font-bold text-indigo-600 hover:bg-indigo-50">Start your free trial</Button></Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
